import { useState, useEffect } from "react";
import { Activity, Users2, Users, TrendingUp } from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from "recharts";

const API = process.env.REACT_APP_BACKEND_URL;

export default function AnalyticsTab({ token, primaryColor }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [fout, setFout] = useState("");
  const gold = primaryColor || "#C9A84C";

  useEffect(() => {
    fetch(`${API}/api/company/analytics`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json())
      .then(d => { setData(d); setLoading(false); })
      .catch(() => { setFout("Analytics laden mislukt"); setLoading(false); });
  }, [token]);

  if (loading) return (
    <div className="space-y-4 max-w-4xl">{[...Array(3)].map((_, i) => <div key={i} className="h-32 rounded-2xl bg-white/3 animate-pulse" />)}</div>
  );

  if (fout) return <div className="p-4 rounded-xl bg-red-500/10 text-red-400 text-sm">{fout}</div>;

  return (
    <div className="max-w-5xl space-y-6">
      <div>
        <h2 className="text-white font-black text-xl">Analytics</h2>
        <p className="text-zinc-500 text-sm">Trainingsoverzicht van de afgelopen 8 weken</p>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        {[
          { label: "Totaal trainingen", value: data?.totaal_trainingen || 0, icon: <Activity size={18} /> },
          { label: "Actieve sporters", value: data?.actieve_sporters || 0, icon: <Users2 size={18} /> },
          { label: "Totaal sporters", value: data?.totaal_sporters || 0, icon: <Users size={18} /> },
          { label: "Gem. per sporter", value: data?.gem_trainingen_per_sporter || 0, icon: <TrendingUp size={18} /> },
        ].map((kpi, i) => (
          <div key={i} className="bg-[#111] border border-white/8 rounded-2xl p-4">
            <span style={{ color: gold }}>{kpi.icon}</span>
            <p className="text-white font-black text-2xl mt-2">{kpi.value}</p>
            <p className="text-zinc-600 text-xs mt-1">{kpi.label}</p>
          </div>
        ))}
      </div>
      {data?.bedrijf_per_week && data.bedrijf_per_week.length > 0 && (
        <div className="bg-[#111] border border-white/8 rounded-2xl p-5">
          <h3 className="text-white font-bold mb-4">Trainingen per week (bedrijfsbreed)</h3>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={data.bedrijf_per_week} margin={{ top: 5, right: 10, left: -20, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
              <XAxis dataKey="week" tick={{ fill: "#71717a", fontSize: 11 }} />
              <YAxis allowDecimals={false} tick={{ fill: "#71717a", fontSize: 11 }} />
              <Tooltip contentStyle={{ background: "#111", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 12, color: "#fff" }} cursor={{ fill: "rgba(255,255,255,0.03)" }} />
              <Bar dataKey="count" fill={gold} radius={[6, 6, 0, 0]} name="Trainingen" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Top Sporters deze maand */}
      {data?.top_sporters_deze_maand && data.top_sporters_deze_maand.length > 0 && (
        <div className="bg-[#111] border border-white/8 rounded-2xl overflow-hidden">
          <div className="px-5 py-4 border-b border-white/5 flex items-center justify-between">
            <div>
              <h3 className="text-white font-bold">Top Sporters</h3>
              <p className="text-zinc-600 text-xs mt-0.5">Meest actief deze maand</p>
            </div>
            <span className="text-xs font-bold px-2.5 py-1 rounded-full" style={{ background: `${gold}15`, color: gold }}>
              {new Date().toLocaleDateString("nl-NL", { month: "long" })}
            </span>
          </div>
          <div className="p-5">
            {data.top_sporters_deze_maand.length >= 1 && (
              <div className="flex items-end justify-center gap-3 mb-5">
                {data.top_sporters_deze_maand[1] && (
                  <div className="flex flex-col items-center gap-2 flex-1">
                    <div className="w-10 h-10 rounded-full flex items-center justify-center text-sm font-black" style={{ background: "rgba(148,163,184,0.15)", color: "#94a3b8" }}>
                      {(data.top_sporters_deze_maand[1].naam || "?")[0].toUpperCase()}
                    </div>
                    <p className="text-white text-xs font-bold text-center truncate w-full px-1">{data.top_sporters_deze_maand[1].naam || "—"}</p>
                    <div className="w-full rounded-t-xl flex flex-col items-center justify-end py-3" style={{ background: "rgba(148,163,184,0.08)", minHeight: 60, border: "1px solid rgba(148,163,184,0.12)" }}>
                      <p className="text-2xl font-black" style={{ color: "#94a3b8" }}>{data.top_sporters_deze_maand[1].count}</p>
                      <p className="text-xs" style={{ color: "#94a3b8" }}>#2</p>
                    </div>
                  </div>
                )}
                <div className="flex flex-col items-center gap-2 flex-1">
                  <div className="w-12 h-12 rounded-full flex items-center justify-center text-base font-black text-black" style={{ background: gold }}>
                    {(data.top_sporters_deze_maand[0].naam || "?")[0].toUpperCase()}
                  </div>
                  <p className="text-white text-xs font-bold text-center truncate w-full px-1">{data.top_sporters_deze_maand[0].naam || "—"}</p>
                  <div className="w-full rounded-t-xl flex flex-col items-center justify-end py-3" style={{ background: `${gold}12`, minHeight: 80, border: `1px solid ${gold}25` }}>
                    <p className="text-3xl font-black" style={{ color: gold }}>{data.top_sporters_deze_maand[0].count}</p>
                    <p className="text-xs font-bold" style={{ color: gold }}>#1</p>
                  </div>
                </div>
                {data.top_sporters_deze_maand[2] && (
                  <div className="flex flex-col items-center gap-2 flex-1">
                    <div className="w-10 h-10 rounded-full flex items-center justify-center text-sm font-black" style={{ background: "rgba(180,120,60,0.15)", color: "#b4783c" }}>
                      {(data.top_sporters_deze_maand[2].naam || "?")[0].toUpperCase()}
                    </div>
                    <p className="text-white text-xs font-bold text-center truncate w-full px-1">{data.top_sporters_deze_maand[2].naam || "—"}</p>
                    <div className="w-full rounded-t-xl flex flex-col items-center justify-end py-3" style={{ background: "rgba(180,120,60,0.08)", minHeight: 44, border: "1px solid rgba(180,120,60,0.15)" }}>
                      <p className="text-2xl font-black" style={{ color: "#b4783c" }}>{data.top_sporters_deze_maand[2].count}</p>
                      <p className="text-xs" style={{ color: "#b4783c" }}>#3</p>
                    </div>
                  </div>
                )}
              </div>
            )}
            {data.top_sporters_deze_maand.slice(3).map((s, i) => (
              <div key={s.id} className="flex items-center gap-3 py-2.5 border-t border-white/5">
                <span className="text-zinc-700 text-xs font-bold w-5 text-center">#{i + 4}</span>
                <div className="w-7 h-7 rounded-full bg-white/5 flex items-center justify-center text-xs font-black text-zinc-400">
                  {(s.naam || "?")[0].toUpperCase()}
                </div>
                <p className="text-zinc-300 text-sm flex-1 truncate">{s.naam || "—"}</p>
                <p className="text-white font-bold text-sm shrink-0">{s.count} <span className="text-zinc-600 text-xs font-normal">tr.</span></p>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="bg-[#111] border border-white/8 rounded-2xl overflow-hidden">
        <div className="p-5 border-b border-white/5"><h3 className="text-white font-bold">Per sporter</h3></div>
        {!data?.sporters || data.sporters.length === 0 ? (
          <div className="p-8 text-center text-zinc-600 text-sm">Nog geen trainingsdata</div>
        ) : (
          <div className="divide-y divide-white/5">
            {data.sporters.map(s => {
              const maxWeek = Math.max(...(s.trainingen_per_week || [1]), 1);
              return (
                <div key={s.id} data-testid={`analytics-sporter-${s.id}`} className="p-4 flex items-center gap-4">
                  <div className="w-9 h-9 rounded-xl shrink-0 flex items-center justify-center text-xs font-black text-black" style={{ background: gold }}>
                    {(s.naam || "?")[0].toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-white font-semibold text-sm">{s.naam}</p>
                    <div className="flex gap-1 mt-1.5">
                      {(s.trainingen_per_week || []).map((cnt, i) => (
                        <div key={i} className="h-2 flex-1 rounded-full"
                          style={{ background: cnt > 0 ? gold : "rgba(255,255,255,0.05)", opacity: cnt > 0 ? 0.4 + (cnt / maxWeek) * 0.6 : 1 }} />
                      ))}
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <p className="text-white font-black text-lg">{s.total_trainingen}</p>
                    <p className="text-zinc-600 text-xs">trainingen</p>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
